import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Button,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";

import {
  getLPE1CheckboxSchema,
  getLPE1FormFieldSchema,
} from "../../../api/lpe1Api";
import { getSignatures } from "../../../api/signaturesApi";
import { trimSignatureImageDataUrl } from "../../../utils/signaturePreview";

/* =========================
   Constants
========================= */

const TOTAL_SECTIONS = 9;

const getToday = () => new Date().toISOString().slice(0, 10);

const SIGNATURE_CAPACITY_OPTIONS = [
  { value: "MA", label: "Managing Agent" },
  { value: "MC", label: "Management Company" },
  { value: "LL", label: "Landlord" },
  { value: "RA", label: "Residents' Association" },
];

/* =========================
   Buffered Inputs (Fix focus)
========================= */

const Text = ({ label, value, onChange, readOnly = false }) => {
  const [local, setLocal] = useState(value ?? "");

  useEffect(() => {
    setLocal(value ?? "");
  }, [value]);

  return (
    <TextField
      label={label}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => !readOnly && onChange(local)}
      InputProps={{ readOnly }}
      fullWidth
      size="small"
    />
  );
};

const SmallText = ({ label, sx, value, onChange, disabled = false }) => {
  const [local, setLocal] = useState(value ?? "");

  useEffect(() => {
    setLocal(value ?? "");
  }, [value]);

  return (
    <TextField
      label={label}
      size="small"
      sx={sx}
      value={local}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => onChange(local)}
    />
  );
};

const Check = ({ label, checked, onChange }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={onChange} />}
    label={label}
  />
);

export const LPE1MetaPanel = ({ meta, onChange }) => {
  const status = meta?.status ?? "";
  const requestedDate = meta?.requestedDate ?? "";
  const dueByDate = meta?.dueByDate ?? "";
  const requestedDateLabel = requestedDate
    ? new Date(requestedDate).toLocaleDateString("en-GB")
    : "";

  const update = (patch) => {
    if (onChange) {
      onChange(patch);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          LPE1 Status
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="lpe1-status-label">Status</InputLabel>
            <Select
              labelId="lpe1-status-label"
              label="Status"
              value={status}
              onChange={(e) => update({ status: e.target.value })}
            >
              <MenuItem value="Requested">Requested</MenuItem>
              <MenuItem value="In-progress">In-progress</MenuItem>
              <MenuItem value="Complete">Complete</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Requested date"
            value={requestedDateLabel}
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
          />

          <TextField
            label="Due by date"
            type="date"
            value={dueByDate}
            onChange={(e) => update({ dueByDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

/* =========================
   Main Component
========================= */

export default function LPE1Section1({ data, setField }) {
  const [section, setSection] = useState(1);
  const [checkboxSchema, setCheckboxSchema] = useState([]);
  const [formFieldSchema, setFormFieldSchema] = useState([]);
  const [signatures, setSignatures] = useState([]);

  const value = (k) => data?.[k] ?? "";
  const set = useCallback((k, v) => setField(k, v), [setField]);
  const sectionOneName = data?.["1.3 Name"];

  useEffect(() => {
    let active = true;
    const loadSchema = async () => {
      try {
        const [checkbox, fields, availableSignatures] = await Promise.all([
          getLPE1CheckboxSchema(),
          getLPE1FormFieldSchema(),
          getSignatures(),
        ]);
        if (active) {
          setCheckboxSchema(checkbox);
          setFormFieldSchema(fields);
          setSignatures((availableSignatures || []).filter((signature) => signature.is_active));
        }
      } catch (err) {
        console.error("Failed to load schema data:", err);
      }
    };
    loadSchema();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!data?.signature_dated) {
      set("signature_dated", getToday());
    }
  }, [data?.signature_dated, set]);

  useEffect(() => {
    if (!data?.signature_capacity) {
      set("signature_capacity", "MA");
    }
  }, [data?.signature_capacity, set]);

  useEffect(() => {
    if (!data?.signature_company && sectionOneName) {
      set("signature_company", sectionOneName);
    }
  }, [data?.signature_company, sectionOneName, set]);

  // Helper for radio-style checkbox groups
  const radio = (name) => ({
    is: (v) => value(name) === v,
    set: (v) => set(name, value(name) === v ? "" : v),
  });

  const formFieldSchemaByKey = useMemo(() => {
    const map = {};
    formFieldSchema.forEach((item) => {
      map[item.field_key] = item;
    });
    return map;
  }, [formFieldSchema]);

  const fieldLabelFor = (fieldKey) =>
    formFieldSchemaByKey[fieldKey]?.question || fieldKey;

  const AutoText = ({ fieldKey, ...props }) => (
    <Text
      label={fieldLabelFor(fieldKey)}
      value={value(fieldKey)}
      onChange={(v) => set(fieldKey, v)}
      {...props}
    />
  );
  const checkboxConfigByKey = useMemo(() => {
    const map = {};
    checkboxSchema.forEach((item) => {
      map[item.key] = item;
    });
    return map;
  }, [checkboxSchema]);

  const renderCheckboxQuestion = (config, opts = {}) => {
    const field = radio(config.key);
    return (
      <Box key={config.key}>
        <Typography variant={opts.questionVariant ?? "subtitle1"}>{config.question}</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {config.options.map((option) => (
            <Check
              key={`${config.key}-${option}`}
              label={option}
              checked={field.is(option)}
              onChange={() => field.set(option)}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const selectedSignatureId = String(value("signature_id") || "");
  const selectedSignature = useMemo(
    () =>
      signatures.find(
        (signature) => String(signature.id) === selectedSignatureId
      ) || null,
    [signatures, selectedSignatureId]
  );

  const selectedSignatureImage =
    selectedSignature?.signature_image_data_url || value("signature_image_data_url");
  const [signaturePreviewImage, setSignaturePreviewImage] = useState("");

  useEffect(() => {
    let active = true;

    const loadPreview = async () => {
      const trimmedImage = await trimSignatureImageDataUrl(selectedSignatureImage);
      if (active) {
        setSignaturePreviewImage(trimmedImage || selectedSignatureImage || "");
      }
    };

    loadPreview();

    return () => {
      active = false;
    };
  }, [selectedSignatureImage]);

  const handleSignatureSelection = (signatureId) => {
    const signature =
      signatures.find((item) => String(item.id) === String(signatureId)) || null;

    set("signature_id", signature ? String(signature.id) : "");
    set("signature_signed", signature?.name || "");
    set("signature_print_name", signature?.name || "");
    set("signature_title", signature?.title || "");
    if (!value("signature_company")) {
      set("signature_company", value("1.3 Name") || "");
    }
  };

  const renderCheckboxByKey = (key, opts) => {
    const config = checkboxConfigByKey[key];
    if (!config) {
      return null;
    }
    return renderCheckboxQuestion(config, opts);
  };

  const outstandingEnforcement = radio("5.6.2iii");
  const remedialActionDateNotApplicable = value("5.6.2iii_date_na") === "N/A";
  const remedialActionDateDisabled = value("5.6.2iii") !== "Yes" || remedialActionDateNotApplicable;

  const setRemedialActionDatePart = (key, nextValue) => {
    if (remedialActionDateNotApplicable) {
      set("5.6.2iii_date_na", "");
    }
    set(key, nextValue);
  };

  const toggleRemedialActionDateNotApplicable = () => {
    const nextValue = remedialActionDateNotApplicable ? "" : "N/A";
    set("5.6.2iii_date_na", nextValue);
    if (nextValue === "N/A") {
      set("5.6.2iii DD", "");
      set("5.6.2iii MM", "");
      set("5.6.2iii YYYY", "");
    }
  };

  /* =========================
     Section 1
  ========================= */

  const Section1 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 1 – Property & Management
        </Typography>

        <Stack spacing={4}>
          <Box>
            <Stack spacing={1.5} mt={1}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <AutoText fieldKey="Property" label="Property" />
                </Box>
                <Box sx={{ width: { xs: "100%", md: 260 } }}>
                  <AutoText fieldKey="PostCode" label="Post Code" />
                </Box>
              </Stack>

              <AutoText fieldKey="Seller" label="Seller" />

              <AutoText fieldKey="UPRN" label="UPRN (if known)" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">1.1 Landlord</Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.1 Name" />
              <AutoText fieldKey="1.1 Address" />
              <AutoText fieldKey="1.1 Telephone" />
              <AutoText fieldKey="1.1 Email" />
              <AutoText fieldKey="1.1 SC" />
              <AutoText fieldKey="1.1 Account Name" />
              <AutoText fieldKey="1.1 Account number" />
              <AutoText fieldKey="1.1 Reference" />
              <AutoText fieldKey="1.1 Fee Type" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">1.2 Management Company</Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.2 Name" />
              <AutoText fieldKey="1.2 Address" />
              <AutoText fieldKey="1.2 Telephone" />
              <AutoText fieldKey="1.2 Email" />
              <AutoText fieldKey="1.2 SC" />
              <AutoText fieldKey="1.2 Account Name" />
              <AutoText fieldKey="1.2 Account number" />
              <AutoText fieldKey="1.2 Reference" />
              <AutoText fieldKey="1.2 Fee Type" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">1.3 Managing Agent</Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.3 Name" />
              <AutoText fieldKey="1.3 Address" />
              <AutoText fieldKey="1.3 Telephone" />
              <AutoText fieldKey="1.3 Email" />
              <Box>{renderCheckboxByKey("appointed_by")}</Box>
              <AutoText fieldKey="1.3 SC" />
              <AutoText fieldKey="1.3 Account Name" />
              <AutoText fieldKey="1.3 Account number" />
              <AutoText fieldKey="1.3 Reference" />
              <AutoText fieldKey="1.3 Fee Type" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">1.4 Residents'/Tenants' Association</Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.4 Name" />
              <AutoText fieldKey="1.4 Address" />
              <AutoText fieldKey="1.4 Telephone" />
              <AutoText fieldKey="1.4 Email" />
              <AutoText fieldKey="1.4 SC" />
              <AutoText fieldKey="1.4 Account Name" />
              <AutoText fieldKey="1.4 Account number" />
              <AutoText fieldKey="1.4 Reference" />
              <AutoText fieldKey="1.4 Fee Type" />
            </Stack>
          </Box>

          {/* <Box>
            <Box>{renderCheckboxByKey("appointed_by")}</Box>
          </Box> */}

          <Box>
            <Typography variant="h6">1.5 Legal Representative of one of the above</Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.5 Name" />
              <AutoText fieldKey="1.5 Address" />
              <AutoText fieldKey="1.5 Telephone" />
              <AutoText fieldKey="1.5 Email" />
              <Box>{renderCheckboxByKey("1.5 appointed_by")}</Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">
              1.6 Who accepts service of the Notice of Assignment &amp; Charge?
            </Typography>
            <Stack spacing={2} mt={1}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>{renderCheckboxByKey("1.6_landlord")}</Box>
                <Box sx={{ flex: 1 }}>
                  <AutoText fieldKey="1.6 Landlord £" />
                </Box>
                <Box>{renderCheckboxByKey("1.6_notice_landlord")}</Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>{renderCheckboxByKey("1.6_management_company")}</Box>
                <Box sx={{ flex: 1 }}>
                  <AutoText fieldKey="1.6 MC £" />
                </Box>
                <Box>{renderCheckboxByKey("1.6_notice_management_company")}</Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>{renderCheckboxByKey("1.6_managing_agent")}</Box>
                <Box sx={{ flex: 1 }}>
                  <AutoText fieldKey="1.6 MA £" />
                </Box>
                <Box>{renderCheckboxByKey("1.6_notice_managing_agent")}</Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>{renderCheckboxByKey("1.6_other")}</Box>
                <Box sx={{ flex: 1 }}>
                  <AutoText fieldKey="1.6 Other £" />
                </Box>
                <Box>{renderCheckboxByKey("1.6_notice_other")}</Box>
              </Stack>
              <AutoText fieldKey="1.6 Name" />
              <AutoText fieldKey="1.6 Address" />
              <AutoText fieldKey="1.6 Telephone" />
              <AutoText fieldKey="1.6 Email" />
              <AutoText fieldKey="1.6 Capacity" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="h6">Responsibilities</Typography>
            <Stack spacing={2} mt={1}>
              <Box>{renderCheckboxByKey("1.7")}</Box>
              <Box>{renderCheckboxByKey("1.8")}</Box>
              <Box>{renderCheckboxByKey("1.9")}</Box>
              <Box>{renderCheckboxByKey("1.10")}</Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle1">
              1.10.1 If the Lessees, please provide the contact details of the
              Lessee in charge
            </Typography>
            <Stack spacing={2} mt={1}>
              <AutoText fieldKey="1.10.1" />

              <Box>{renderCheckboxByKey("1.11")}</Box>

              <Box>{renderCheckboxByKey("1.12")}</Box>
          </Stack>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

  /* =========================
     Section 2
  ========================= */

  const Section2 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 2 – Consents & Covenants
        </Typography>

        <Stack spacing={3}>
          <Box>{renderCheckboxByKey("2.1")}</Box>

          <AutoText fieldKey="2.1.1 £" />
          <AutoText fieldKey="2.1.2" />

          <Box>{renderCheckboxByKey("2.2")}</Box>

          <AutoText fieldKey="2.3" />

          <Box>{renderCheckboxByKey("2.4")}</Box>

          <AutoText fieldKey="2.4.1" />

          <Box>{renderCheckboxByKey("2.5")}</Box>

          <AutoText fieldKey="2.5.1" />

          <AutoText fieldKey="2.6" />
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 3
  ========================= */

  const Section3 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 3 – Ground Rent
        </Typography>

        <Stack spacing={3}>
          <AutoText fieldKey="3.1 £" />
          <Box>{renderCheckboxByKey("3.2")}</Box>

          <AutoText fieldKey="3.2.1" />

          <Box>
            <Typography variant="subtitle1">3.3 What period is covered by the last demand?</Typography>
            <Stack direction="row" spacing={3} mt={1} alignItems="center">
              <Typography>From:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("3.3 From DD")} onChange={(v) => set("3.3 From DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("3.3 From MM")} onChange={(v) => set("3.3 From MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("3.3 From YYYY")} onChange={(v) => set("3.3 From YYYY", v)} />
              <Typography>To:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("3.3 To DD")} onChange={(v) => set("3.3 To DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("3.3 To MM")} onChange={(v) => set("3.3 To MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("3.3 To YYYY")} onChange={(v) => set("3.3 To YYYY", v)} />
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 4
  ========================= */

  const Section4 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 4 – Service Charge
        </Typography>

        <Stack spacing={3}>
          <AutoText fieldKey="4.1" />
          <AutoText fieldKey="411 £" />
          <AutoText fieldKey="412" />

          <Box>{renderCheckboxByKey("4.2")}</Box>

          <AutoText fieldKey="4.2.1" />

          <Box>{renderCheckboxByKey("4.3")}</Box>

          <AutoText fieldKey="4.3.1 From" />

          <Box>
            <Typography variant="subtitle1">4.4 What period is covered by the last demand?</Typography>
            <Stack direction="row" spacing={3} mt={1} alignItems="center">
              <Typography>From:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("4.4 From DD")} onChange={(v) => set("4.4 From DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("4.4 From MM")} onChange={(v) => set("4.4 From MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.4 From YYYY")} onChange={(v) => set("4.4 From YYYY", v)} />
              <Typography>To:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("4.4 To DD")} onChange={(v) => set("4.4 To DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("4.4 To MM")} onChange={(v) => set("4.4 To MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.4 To YYYY")} onChange={(v) => set("4.4 To YYYY", v)} />
            </Stack>
          </Box>

          <Box>{renderCheckboxByKey("4.5")}</Box>

          <AutoText fieldKey="4.5.1" />

          <Box>{renderCheckboxByKey("4.6")}</Box>

          <AutoText fieldKey="4.6.1 £" />

          <Box>{renderCheckboxByKey("4.6.2")}</Box>

          <AutoText fieldKey="4.6.3" />

          <Box>
            <Typography variant="subtitle1">4.7 Date when the Managed Areas were last decorated</Typography>
            <Stack spacing={2} mt={1}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Typography sx={{ width: 100 }}>Internally:</Typography>
                <SmallText label="DD" sx={{ width: 80 }} value={value("4.7 ID DD")} onChange={(v) => set("4.7 ID DD", v)} />
                <SmallText label="MM" sx={{ width: 80 }} value={value("4.7 ID MM")} onChange={(v) => set("4.7 ID MM", v)} />
                <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.7 ID YYYY")} onChange={(v) => set("4.7 ID YYYY", v)} />
                <Typography>To:</Typography>
                <SmallText label="DD" sx={{ width: 80 }} value={value("4.7 ID TO DD")} onChange={(v) => set("4.7 ID TO DD", v)} />
                <SmallText label="MM" sx={{ width: 80 }} value={value("4.7 ID TO MM")} onChange={(v) => set("4.7 ID TO MM", v)} />
                <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.7 ID TO YYYY")} onChange={(v) => set("4.7 ID TO YYYY", v)} />
              </Stack>
              <Stack direction="row" spacing={3} alignItems="center">
                <Typography sx={{ width: 100 }}>Externally:</Typography>
                <SmallText label="DD" sx={{ width: 80 }} value={value("4.7 ED DD")} onChange={(v) => set("4.7 ED DD", v)} />
                <SmallText label="MM" sx={{ width: 80 }} value={value("4.7 ED MM")} onChange={(v) => set("4.7 ED MM", v)} />
                <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.7 ED YYYY")} onChange={(v) => set("4.7 ED YYYY", v)} />
                <Typography>To:</Typography>
                <SmallText label="DD" sx={{ width: 80 }} value={value("4.7 ED TO DD")} onChange={(v) => set("4.7 ED TO DD", v)} />
                <SmallText label="MM" sx={{ width: 80 }} value={value("4.7 ED TO MM")} onChange={(v) => set("4.7 ED TO MM", v)} />
                <SmallText label="YYYY" sx={{ width: 100 }} value={value("4.7 ED TO YYYY")} onChange={(v) => set("4.7 ED TO YYYY", v)} />
              </Stack>
            </Stack>
          </Box>

          <Box>{renderCheckboxByKey("4.8")}</Box>

          <AutoText fieldKey="4.8.1" />

          <Box>{renderCheckboxByKey("4.9")}</Box>

          <AutoText fieldKey="4.9.1" />

          <Box>{renderCheckboxByKey("4.10")}</Box>

          <AutoText fieldKey="4.10.1" />

          <Box>{renderCheckboxByKey("4.11")}</Box>

          <AutoText fieldKey="4.11.1" />

          <Box>{renderCheckboxByKey("4.12")}</Box>

          <AutoText fieldKey="4.12.1" />

          <Box>{renderCheckboxByKey("4.13")}</Box>

          <AutoText fieldKey="4.13.1" />

          <Box>{renderCheckboxByKey("4.14")}</Box>

          <Box>{renderCheckboxByKey("4.15")}</Box>
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 5
  ========================= */

  const Section5 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 5 – Buildings Insurance
        </Typography>

        <Stack spacing={3}>
          <Box>{renderCheckboxByKey("5.1")}</Box>

          <AutoText fieldKey="5.1.1" />

          <Box>
            <Typography variant="subtitle1">5.2 What period is covered by the last demand?</Typography>
            <Stack direction="row" spacing={3} mt={1} alignItems="center">
              <Typography>From:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("5.2 From DD")} onChange={(v) => set("5.2 From DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("5.2 From MM")} onChange={(v) => set("5.2 From MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("5.2 From YYYY")} onChange={(v) => set("5.2 From YYYY", v)} />
              <Typography>To:</Typography>
              <SmallText label="DD" sx={{ width: 80 }} value={value("5.2 To DD")} onChange={(v) => set("5.2 To DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("5.2 To MM")} onChange={(v) => set("5.2 To MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("5.2 To YYYY")} onChange={(v) => set("5.2 To YYYY", v)} />
            </Stack>
          </Box>

          <Box>{renderCheckboxByKey("5.3")}</Box>

          <AutoText fieldKey="5.3.1" />

          <Box>{renderCheckboxByKey("5.4")}</Box>

          <AutoText fieldKey="5.4.1" />

          <Box>{renderCheckboxByKey("5.5")}</Box>

          <AutoText fieldKey="5.5.1" />

          <Box>{renderCheckboxByKey("5.6")}</Box>

          <Box>{renderCheckboxByKey("5.6.1i")}</Box>

          <Box>{renderCheckboxByKey("5.6.1ii")}</Box>

          <Box>{renderCheckboxByKey("5.6.2i")}</Box>

          <Box>{renderCheckboxByKey("5.6.2ii")}</Box>

          <Box>
            <Typography variant="subtitle1">
              5.6.2 (iii) Is there any outstanding enforcement action against the landlord or accountable person (such as outstanding enforcement notices)?
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              {["Yes", "No", "N/A"].map((option) => (
                <Check
                  key={`5.6.2iii-${option}`}
                  label={option}
                  checked={outstandingEnforcement.is(option)}
                  onChange={() => outstandingEnforcement.set(option)}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle1">If Yes, by what date is this remedial action required?</Typography>
            <Stack direction="row" spacing={3} mt={1} alignItems="center" flexWrap="wrap">
              <SmallText
                label="DD"
                sx={{ width: 80 }}
                value={value("5.6.2iii DD")}
                onChange={(v) => setRemedialActionDatePart("5.6.2iii DD", v)}
                disabled={remedialActionDateDisabled}
              />
              <SmallText
                label="MM"
                sx={{ width: 80 }}
                value={value("5.6.2iii MM")}
                onChange={(v) => setRemedialActionDatePart("5.6.2iii MM", v)}
                disabled={remedialActionDateDisabled}
              />
              <SmallText
                label="YYYY"
                sx={{ width: 100 }}
                value={value("5.6.2iii YYYY")}
                onChange={(v) => setRemedialActionDatePart("5.6.2iii YYYY", v)}
                disabled={remedialActionDateDisabled}
              />
              <Check
                label="N/A"
                checked={remedialActionDateNotApplicable}
                onChange={toggleRemedialActionDateNotApplicable}
              />
            </Stack>
          </Box>

          <Box>{renderCheckboxByKey("5.7")}</Box>

          <AutoText fieldKey="5.7.1" />

          <Box>{renderCheckboxByKey("5.8")}</Box>

          <AutoText fieldKey="5.8.1" />

          <Box>
            <Typography variant="subtitle1">5.9 Please confirm the date of the last buildings reinstatement cost assessment</Typography>
            <Stack direction="row" spacing={3} mt={1} alignItems="center">
              <SmallText label="DD" sx={{ width: 80 }} value={value("5.9 DD")} onChange={(v) => set("5.9 DD", v)} />
              <SmallText label="MM" sx={{ width: 80 }} value={value("5.9 MM")} onChange={(v) => set("5.9 MM", v)} />
              <SmallText label="YYYY" sx={{ width: 100 }} value={value("5.9 YYYY")} onChange={(v) => set("5.9 YYYY", v)} />
            </Stack>
          </Box>

          <Box>{renderCheckboxByKey("5.10")}</Box>

          <AutoText fieldKey="5.10.1 £" />
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 6
  ========================= */

  const Section6 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 6 – Disputes & Enforcement
        </Typography>

        <Stack spacing={3}>
          <Box>{renderCheckboxByKey("6.1")}</Box>

          <Box>{renderCheckboxByKey("6.2")}</Box>

          <AutoText fieldKey="6.2.1" />

          <Box>{renderCheckboxByKey("6.3")}</Box>

          <AutoText fieldKey="6.3.1" />

          <Box>{renderCheckboxByKey("6.4")}</Box>

          <AutoText fieldKey="6.4.1" />
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 7
  ========================= */

  const Section7 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 7 – General
        </Typography>

        <Stack spacing={3}>
          <AutoText fieldKey="7.1" />

          <Box>{renderCheckboxByKey("7.2")}</Box>

          <AutoText fieldKey="7.2.1" />

          <Box>{renderCheckboxByKey("7.3")}</Box>

          <AutoText fieldKey="7.3.1" />

          <Box>{renderCheckboxByKey("7.4")}</Box>

          <AutoText fieldKey="7.4.1" />

          <Box>{renderCheckboxByKey("7.5_restriction")}</Box>

          <AutoText fieldKey="7.5" />
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Section 8
  ========================= */

  const Section8 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Section 8 – Required Documents
        </Typography>

        <Stack spacing={3}>
          <Box>{renderCheckboxByKey("8.1")}</Box>

          <Box>{renderCheckboxByKey("8.2")}</Box>

          <Box>{renderCheckboxByKey("8.3")}</Box>

          <Box>{renderCheckboxByKey("8.4")}</Box>

          <Box>{renderCheckboxByKey("8.5")}</Box>

          <Box>{renderCheckboxByKey("8.6")}</Box>

          <Box>{renderCheckboxByKey("8.7")}</Box>

          <Box>{renderCheckboxByKey("8.8")}</Box>

          <Box>{renderCheckboxByKey("8.9")}</Box>

          <Box>{renderCheckboxByKey("8.10")}</Box>

          <Box>{renderCheckboxByKey("8.11")}</Box>
          <Box>{renderCheckboxByKey("8.11_sub")}</Box>
          <AutoText fieldKey="8.11 Fee" />

          <Box>{renderCheckboxByKey("8.12")}</Box>

          <Box>{renderCheckboxByKey("8.13")}</Box>

          <Box>{renderCheckboxByKey("8.14")}</Box>

          <Box>{renderCheckboxByKey("8.15")}</Box>

          <Box>{renderCheckboxByKey("8.16")}</Box>

          <Box>{renderCheckboxByKey("8.17")}</Box>

          <Box>{renderCheckboxByKey("8.18")}</Box>

          <Box>{renderCheckboxByKey("8.19")}</Box>

          <Box>{renderCheckboxByKey("8.20")}</Box>

          <Box>{renderCheckboxByKey("8.21")}</Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const Section9 = () => (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Signature Page
        </Typography>

        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="lpe1-signature-select-label">Signature</InputLabel>
            <Select
              labelId="lpe1-signature-select-label"
              label="Signature"
              value={selectedSignatureId}
              onChange={(event) => handleSignatureSelection(event.target.value)}
            >
              <MenuItem value="">Select signature</MenuItem>
              {signatures.map((signature) => (
                <MenuItem key={signature.id} value={String(signature.id)}>
                  {signature.name} - {signature.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRight: { xs: "none", md: "1px solid" },
                  borderBottom: { xs: "1px solid", md: "none" },
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Signature
                </Typography>
                <Box
                  sx={{
                    minHeight: 96,
                    borderBottom: "2px dotted",
                    borderColor: "text.primary",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-start",
                    pb: 0.5,
                  }}
                >
                  {signaturePreviewImage ? (
                    <Box
                      component="img"
                      src={signaturePreviewImage}
                      alt={`${value("signature_print_name") || selectedSignature?.name || "Selected"} signature`}
                      sx={{ maxHeight: 84, maxWidth: "96%", objectFit: "contain", display: "block" }}
                    />
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                <TextField
                  label="Dated"
                  type="date"
                  value={value("signature_dated")}
                  onChange={(event) => set("signature_dated", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRight: { xs: "none", md: "1px solid" },
                  borderBottom: { xs: "1px solid", md: "none" },
                  borderColor: "divider",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Print Name
                    </Typography>
                    <Box
                      sx={{
                        minHeight: 34,
                        borderBottom: "2px dotted",
                        borderColor: "text.primary",
                        display: "flex",
                        alignItems: "flex-end",
                        pb: 0.5,
                      }}
                    >
                      <Typography>{value("signature_print_name")}</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Company
                    </Typography>
                    <Box
                      sx={{
                        minHeight: 34,
                        borderBottom: "2px dotted",
                        borderColor: "text.primary",
                        display: "flex",
                        alignItems: "flex-end",
                        pb: 0.5,
                      }}
                    >
                      <Typography>{value("signature_company")}</Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    mb: 2,
                  }}
                >
                  Please tick as applicable below, to confirm the capacity in which the answers are given.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  {SIGNATURE_CAPACITY_OPTIONS.map((option) => (
                    <Check
                      key={option.value}
                      label={option.label}
                      checked={value("signature_capacity") === option.value}
                      onChange={() =>
                        set(
                          "signature_capacity",
                          value("signature_capacity") === option.value ? "" : option.value
                        )
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  /* =========================
     Navigation
  ========================= */

  return (
    <Box maxWidth="900px" mx="auto">
      <Tabs
        value={section - 1}
        onChange={(event, value) => setSection(value + 1)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {Array.from({ length: TOTAL_SECTIONS }, (_, i) => (
          <Tab
            key={i + 1}
            label={i + 1 === 9 ? "Signature Page" : `Section ${i + 1}`}
          />
        ))}
      </Tabs>

      {section === 1 && <Section1 />}
      {section === 2 && <Section2 />}
      {section === 3 && <Section3 />}
      {section === 4 && <Section4 />}
      {section === 5 && <Section5 />}
      {section === 6 && <Section6 />}
      {section === 7 && <Section7 />}
      {section === 8 && <Section8 />}
      {section === 9 && <Section9 />}

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          disabled={section === 1}
          onClick={() => setSection((s) => Math.max(1, s - 1))}
        >
          Previous Page
        </Button>

        <Typography color="text.secondary">
          Section {section} of {TOTAL_SECTIONS}
        </Typography>

        <Button
          variant="contained"
          disabled={section === TOTAL_SECTIONS}
          onClick={() => setSection((s) => Math.min(TOTAL_SECTIONS, s + 1))}
        >
          Next Page
        </Button>
      </Box>
    </Box>
  );
}
